import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentPage() {
  const navigate = useNavigate();
  const { token, user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [paymentId, setPaymentId] = useState('');
  const [showPaymentIdInput, setShowPaymentIdInput] = useState(true); // Always show input field

  useEffect(() => {
    // Load Razorpay button script inside a form
    const buttonContainer = document.getElementById('razorpay-button-container');
    if (buttonContainer) {
      // Create form element
      const form = document.createElement('form');
      
      // Create script element
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/payment-button.js';
      script.setAttribute('data-payment_button_id', 'pl_RCkyaNaogXhsZu');
      script.async = true;
      
      // Append script to form
      form.appendChild(script);
      
      // Append form to container
      buttonContainer.appendChild(form);
    }

    // Register pending payment
    registerPendingPayment();

    return () => {
      if (buttonContainer) {
        buttonContainer.innerHTML = '';
      }
    };
  }, []);

  const registerPendingPayment = async () => {
    try {
      await axios.post(
        `${API}/payment/register-pending`,
        { phone_number: user?.phone_number },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Failed to register pending payment:', error);
    }
  };

  const checkPaymentStatus = async () => {
    if (!paymentId || paymentId.trim().length < 10) {
      toast.error('Please enter a valid Razorpay Payment ID (starts with pay_)');
      return;
    }

    setLoading(true);
    try {
      // Add credits with payment ID verification
      const response = await axios.post(
        `${API}/payment/complete`,
        { payment_id: paymentId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      updateUser({ analyses_remaining: response.data.analyses_remaining });
      toast.success('Payment verified! You now have ' + response.data.analyses_remaining + ' analyses.');
      
      // Redirect to analyze page after 1 second
      setTimeout(() => {
        navigate('/analyze');
      }, 1000);
    } catch (error) {
      console.error('Complete payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to verify payment. Please check your Payment ID.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center">
          <Button
            data-testid="back-button"
            variant="ghost"
            size="icon"
            onClick={() => navigate('/')}
            className="rounded-full mr-4"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <span className="text-xl font-bold text-slate-900 dark:text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Resume Rx - Payment
          </span>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Get 3 Resume Analyses
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            Optimize your resume with AI-powered insights
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Pricing Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border-2 border-emerald-500 p-8"
          >
            <div className="text-center mb-6">
              <div className="inline-block bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                BEST VALUE
              </div>
              <div className="text-5xl font-bold text-slate-900 dark:text-white mb-2">
                ₹499
              </div>
              <p className="text-slate-600 dark:text-slate-400">For 3 Analyses</p>
              <p className="text-sm text-slate-500 dark:text-slate-500 mt-2">₹166 per analysis</p>
            </div>

            <div className="space-y-4 mb-8">
              {[
                'ATS Compatibility Score (0-100)',
                'Prioritized Fixes with Impact Levels',
                'Keyword Gap Analysis',
                'AI-Powered Content Rewrites',
                'Export to TXT & PDF',
                'Section-by-Section Review'
              ].map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 dark:text-slate-300">{feature}</span>
                </div>
              ))}
            </div>

            {/* Razorpay Payment Button */}
            <div id="razorpay-button-container" className="mb-4" data-testid="razorpay-button-container">
              {/* Razorpay button will be injected here */}
            </div>

            {/* Payment ID Input - Always visible now */}
            {showPaymentIdInput && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="space-y-4 mt-6 p-4 bg-blue-50 dark:bg-blue-950/30 border-2 border-blue-300 dark:border-blue-700 rounded-lg"
              >
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded p-3 mb-3">
                  <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-300">
                    📌 IMPORTANT: Complete these steps in order:
                  </p>
                  <ol className="text-xs text-yellow-700 dark:text-yellow-400 mt-2 space-y-1 list-decimal list-inside">
                    <li>Click "Pay Now" button above</li>
                    <li>Complete payment on Razorpay</li>
                    <li>Copy Payment ID from receipt/SMS (starts with "pay_")</li>
                    <li>Paste it below and click "Verify Payment"</li>
                  </ol>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Razorpay Payment ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    data-testid="payment-id-input"
                    placeholder="pay_xxxxxxxxxxxxxx"
                    value={paymentId}
                    onChange={(e) => setPaymentId(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border-2 border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-base"
                  />
                </div>

                <Button
                  data-testid="verify-payment-button"
                  onClick={checkPaymentStatus}
                  disabled={loading}
                  className="w-full bg-emerald-500 text-white hover:bg-emerald-600 rounded-full py-4 text-base font-semibold shadow-lg hover:shadow-xl"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Verifying Payment...
                    </>
                  ) : (
                    'Verify Payment & Get 3 Analyses'
                  )}
                </Button>
              </motion.div>
            )}

            <p className="text-xs text-center text-slate-500 dark:text-slate-500 mt-4">
              Secure payment powered by Razorpay
            </p>
          </motion.div>

          {/* Info Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div className="bg-white dark:bg-slate-900 rounded-xl p-6 border border-slate-200 dark:border-slate-800">
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Why Resume Rx?
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Most resumes never reach human eyes. They're filtered by Applicant Tracking Systems (ATS) that scan for keywords, formatting, and relevance.
              </p>
              <p className="text-slate-600 dark:text-slate-400">
                Resume Rx uses AI to analyze your resume against job descriptions, identifying gaps and providing actionable fixes that get you past ATS filters and into interviews.
              </p>
            </div>

            <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-xl p-6 border border-emerald-200 dark:border-emerald-800">
              <h3 className="text-xl font-semibold text-emerald-900 dark:text-emerald-300 mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <CheckCircle className="w-6 h-6" />
                Supporting a Good Cause
              </h3>
              <p className="text-emerald-800 dark:text-emerald-400">
                100% of proceeds support therapy programs for autistic children. 
                Get your dream job while making a difference! 💚
              </p>
            </div>

            <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-3">
                Frequently Asked Questions
              </h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">How long do credits last?</p>
                  <p className="text-slate-600 dark:text-slate-400">Your 3 analyses never expire. Use them whenever you need.</p>
                </div>
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">When will I get my credits?</p>
                  <p className="text-slate-600 dark:text-slate-400">Instantly after entering your Razorpay Payment ID! Complete payment, copy the payment ID from your receipt, enter it, and get 3 analyses immediately.</p>
                </div>
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">Where do I find my Payment ID?</p>
                  <p className="text-slate-600 dark:text-slate-400">After successful payment, Razorpay shows a receipt with Payment ID (starts with "pay_"). You'll also receive it via SMS/email.</p>
                </div>
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">Is my data secure?</p>
                  <p className="text-slate-600 dark:text-slate-400">Absolutely. We never share your resume data with anyone.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
